/**
 * Delivery Estimate components for Product Page and Cart Drawer
 * – Reads saved postcode from localStorage (shared with delivery-location.js)
 * – Listens for delivery-location:changed event from the shared modal
 * – PDP: shows location + weight-based item count messaging
 * – Cart: shows Qwiki delivery dates/spend milestones locally, carrier rates elsewhere
 */

// Shared utilities
const DeliveryUtils = {
  STORAGE_KEY: 'ap:delivery_location',
  SHIPPING_SHARED_KEY: 'ap:shipping_calculator:shared_v1',

  nextQwikiDelivery(zone, config, now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: config.timeZone,
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', hourCycle: 'h23'
    }).formatToParts(now);
    const part = (type) => Number(parts.find((value) => value.type === type).value);
    // UTC is used only for calendar arithmetic; the day/hour come from Melbourne.
    const date = new Date(Date.UTC(part('year'), part('month') - 1, part('day')));
    let days = (zone.weekday - date.getUTCDay() + 7) % 7;
    if (days === 0 && part('hour') >= config.cutoffHour) days = 7;
    date.setUTCDate(date.getUTCDate() + days);
    return date;
  },

  // Weight tier boundaries in grams - merged across all carriers:
  // Carrier 1 (Couriers Please): 300g, 500g, 1kg, 3kg, 5kg
  // AusPost eParcel: 500g, 1kg, 3kg, 5kg
  // Direct Freight: continuous per-kg (flat zone ceiling varies by zone - handled separately)
  WEIGHT_TIERS: [300, 500, 1000, 3000, 5000],

  // Packaging weight estimate based on product weight (in grams)
  estimatePackaging(productWeightG) {
    if (productWeightG <= 500) return 200;
    if (productWeightG <= 2000) return 270;
    if (productWeightG <= 5000) return 500;
    if (productWeightG <= 10000) return 700;
    return 1000;
  },

  // Get total shipping weight (product + packaging)
  shippingWeight(cartWeightG) {
    return cartWeightG + this.estimatePackaging(cartWeightG);
  },

  // Cache shipping rate data to build price tier map
  RATE_CACHE_KEY: 'ap:shipping_rates_cache',
  cacheRate(postcode, weightG, price) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.RATE_CACHE_KEY) || '{}');
      if (!cache[postcode]) cache[postcode] = [];

      // Add or update entry for this weight
      const existing = cache[postcode].findIndex(r => r.weight === weightG);
      if (existing >= 0) {
        cache[postcode][existing].price = price;
      } else {
        cache[postcode].push({ weight: weightG, price });
      }

      localStorage.setItem(this.RATE_CACHE_KEY, JSON.stringify(cache));
    } catch (e) { }
  },

  // Cache variant weight data to avoid repeated API calls
  VARIANT_CACHE_KEY: 'ap:variant_weights_cache',
  cacheVariantWeight(variantId, weight) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.VARIANT_CACHE_KEY) || '{}');
      cache[variantId] = { weight, cached_at: Date.now() };
      localStorage.setItem(this.VARIANT_CACHE_KEY, JSON.stringify(cache));
    } catch (e) { }
  },

  getVariantWeightFromCache(variantId) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.VARIANT_CACHE_KEY) || '{}');
      return cache[variantId] ? cache[variantId].weight : null;
    } catch (e) {
      return null;
    }
  },

  async fetchVariantWeights(variantIds) {
    const weights = {};

    // Check cache first
    for (const id of variantIds) {
      const cached = this.getVariantWeightFromCache(id);
      if (cached !== null) {
        weights[id] = cached;
      }
    }

    // Check injected data from Liquid template
    const injectedWeights = window.cartVariantWeights || {};
    for (const id of variantIds) {
      if (!weights[id] && injectedWeights[id] !== undefined) {
        weights[id] = injectedWeights[id];
        this.cacheVariantWeight(id, injectedWeights[id]);
      }
    }

    // Fill in any missing weights with 0 (fallback)
    for (const id of variantIds) {
      if (weights[id] === undefined) {
        weights[id] = 0;
      }
    }

    return weights;
  },

  // Get next price increase from cache
  getNextPriceIncrease(postcode, currentWeightG, currentPrice) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.RATE_CACHE_KEY) || '{}');
      const rates = (cache[postcode] || []).sort((a, b) => a.weight - b.weight);

      // Find next weight where price increases (look at weights BEYOND current)
      for (const rate of rates) {
        if (rate.weight > currentWeightG && parseFloat(rate.price) > currentPrice) {
          return { weight: rate.weight, price: rate.price };
        }
      }

      // Also check if we have multiple data points (indicating cache is active)
      // and suggest weight progression instead of misleading tier info
      if (rates.length >= 2) {
        return 'cache_active'; // Signal that cache exists but no price jump yet
      }
    } catch (e) { }
    return null;
  },

  // Find the current tier ceiling and how much headroom remains
  tierHeadroom(cartWeightG) {
    const totalWeight = this.shippingWeight(cartWeightG);

    for (const tier of this.WEIGHT_TIERS) {
      if (totalWeight <= tier) {
        return {
          currentTier: tier,
          headroomG: Math.max(0, tier - totalWeight),
          totalWeight: totalWeight,
        };
      }
    }

    // Over 5kg - per-kg pricing, next "tier" is the next whole kg
    const nextKg = Math.ceil(totalWeight / 1000) * 1000;
    return {
      currentTier: nextKg,
      headroomG: Math.max(0, nextKg - totalWeight),
      totalWeight: totalWeight,
    };
  },

  // Get weight progress toward next tier (returns percentage and absolute values)
  getWeightProgress(cartWeightG) {
    const info = this.tierHeadroom(cartWeightG);
    const percentUsed = (info.totalWeight / info.currentTier) * 100;

    return {
      current: info.totalWeight,
      ceiling: info.currentTier,
      headroom: info.headroomG,
      percentUsed: Math.min(100, percentUsed), // Cap at 100%
    };
  },

  stateFromPostcode(pc) {
    const n = parseInt(pc, 10);
    if (n === 2899) return { country: 'Norfolk Island', province: '', short: 'NF' };
    if (n >= 2600 && n <= 2618) return { country: 'Australia', province: 'Australian Capital Territory', short: 'ACT' };
    if (n >= 2900 && n <= 2920) return { country: 'Australia', province: 'Australian Capital Territory', short: 'ACT' };
    if (n >= 800 && n <= 999) return { country: 'Australia', province: 'Northern Territory', short: 'NT' };
    if (n >= 1000 && n <= 2599) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
    if (n >= 2619 && n <= 2898) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
    if (n >= 2900 && n <= 2999) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
    if (n >= 3000 && n <= 3999) return { country: 'Australia', province: 'Victoria', short: 'VIC' };
    if (n >= 8000 && n <= 8999) return { country: 'Australia', province: 'Victoria', short: 'VIC' };
    if (n >= 4000 && n <= 4999) return { country: 'Australia', province: 'Queensland', short: 'QLD' };
    if (n >= 9000 && n <= 9999) return { country: 'Australia', province: 'Queensland', short: 'QLD' };
    if (n >= 5000 && n <= 5999) return { country: 'Australia', province: 'South Australia', short: 'SA' };
    if (n >= 6000 && n <= 6797) return { country: 'Australia', province: 'Western Australia', short: 'WA' };
    if (n === 6798) return { country: 'Christmas Island', province: '', short: 'CX' };
    if (n === 6799) return { country: 'Cocos (Keeling) Islands', province: '', short: 'CC' };
    if (n >= 6800 && n <= 6999) return { country: 'Australia', province: 'Western Australia', short: 'WA' };
    if (n >= 7000 && n <= 7999) return { country: 'Australia', province: 'Tasmania', short: 'TAS' };
    return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
  },

  readLocation() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  },

  getLocationLabel(stored) {
    if (!stored || !stored.postcode) return null;
    const suburb = stored.suburb || this.stateFromPostcode(stored.postcode).short;
    return `${suburb} ${stored.postcode}`;
  },
};


/**
 * Product Page - Delivery Estimate
 */
if (!customElements.get('delivery-estimate-pdp')) {
  class DeliveryEstimatePDP extends HTMLElement {
    connectedCallback() {
      this.locationEl = this.querySelector('.js-pdp-location');
      this.shippingMsg = this.querySelector('.js-pdp-shipping-msg');
      this.productWeight = parseFloat(this.dataset.productWeight) || 0;

      this.restore();

      // Listen for location changes from the shared modal
      document.addEventListener('delivery-location:changed', (e) => {
        this.applyLocation(e.detail);
      });

      // Listen for variant changes to update the weight-based messaging
      document.addEventListener('on:variant:change', (e) => {
        const variant = e.detail && e.detail.variant;
        if (variant && variant.weight != null) {
          this.productWeight = variant.weight;
          const stored = DeliveryUtils.readLocation();
          if (stored) this.applyLocation(stored);
        }
      });
    }

    restore() {
      const stored = DeliveryUtils.readLocation();
      if (stored && stored.postcode) {
        this.applyLocation(stored);
      }
    }

    applyLocation(stored) {
      const label = DeliveryUtils.getLocationLabel(stored);
      if (label && this.locationEl) {
        this.locationEl.textContent = label;
      }

    }
  }
  customElements.define('delivery-estimate-pdp', DeliveryEstimatePDP);
}


/**
 * Cart Drawer - Delivery Estimate
 */
if (!customElements.get('delivery-estimate-cart')) {
  class DeliveryEstimateCart extends HTMLElement {
    connectedCallback() {
      this.locationEl = this.querySelector('.js-cart-location');
      this.rateContainer = this.querySelector('.js-cart-rate');
      this.rateText = this.querySelector('.js-cart-rate-text');
      this.perItemEl = this.querySelector('.js-cart-per-item');
      this.headroomEl = this.querySelector('.js-cart-headroom');
      this.loadingEl = this.querySelector('.js-cart-loading');
      this.qwikiConfig = JSON.parse(this.querySelector('.js-qwiki-config').textContent);
      this.localContainer = this.querySelector('.js-cart-local');
      this.localZoneEl = this.querySelector('.js-local-zone');
      this.localDateEl = this.querySelector('.js-local-date');
      this.milestone = this.querySelector('.js-cart-milestone');
      this.milestoneMsg = this.querySelector('.js-milestone-msg');
      this.milestoneFill = this.querySelector('.js-milestone-fill');
      this.milestoneProgress = this.querySelector('.js-milestone-progress');
      this.rateFooter = this.querySelector('.js-rate-footer');
      this.rateFooterLabel = this.querySelector('.dec__rate-footer-label');
      this.rateFooterPrice = this.querySelector('.js-rate-footer-price');
      this.moreText = this.querySelector('.js-more-text');
      this.defaultMoreText = this.moreText.textContent;
      this.poboxNotice = this.querySelector('.dec__pobox-notice');
      this._onQwikiLinkClick = (event) => this.handleQwikiLinkClick(event);
      this.addEventListener('click', this._onQwikiLinkClick);

      this.restore();

      // Store bound handlers so disconnectedCallback can remove them
      this._onLocationChanged = (e) => this.fetchAndDisplay(e.detail.postcode);
      this._onCartUpdated = () => this.refreshCart();

      // [May 9, 2026] MutationObserver on cart-drawer's `open` attribute.
      // The drawer can be opened via several paths (dispatch event, direct
      // .open() from click handlers, programmatic re-render). All paths
      // land at DockableSideDrawer.open() which calls setAttribute('open').
      // Watching this attribute catches every open path - more reliable
      // than the dispatch:cart-drawer:open event which some paths skip.
      this._onDrawerOpen = () => {
        const stored = DeliveryUtils.readLocation();
        if (stored && stored.postcode) {
          this.fetchAndDisplay(stored.postcode);
        }
      };
      this._cartDrawerEl = this.closest('cart-drawer') || document.getElementById('cart-drawer');
      if (this._cartDrawerEl) {
        if (this._cartDrawerEl.hasAttribute('open')) {
          this._onDrawerOpen();
        }
        this._drawerOpenObserver = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if (m.type === 'attributes' && m.attributeName === 'open') {
              if (this._cartDrawerEl.hasAttribute('open')) {
                this._onDrawerOpen();
              }
            }
          }
        });
        this._drawerOpenObserver.observe(this._cartDrawerEl, {
          attributes: true,
          attributeFilter: ['open']
        });
      }

      document.addEventListener('delivery-location:changed', this._onLocationChanged);
      document.addEventListener('cart:updated', this._onCartUpdated);
      document.addEventListener('cart:change', this._onCartUpdated);
      // on:line-item:change fires AFTER cart-items.js section render finishes,
      // which is the reliable signal that the cart DOM is fully synced.
      document.addEventListener('on:line-item:change', this._onCartUpdated);
      document.addEventListener('dispatch:cart-drawer:open', this._onDrawerOpen);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onQwikiLinkClick);
      document.removeEventListener('delivery-location:changed', this._onLocationChanged);
      document.removeEventListener('cart:updated', this._onCartUpdated);
      document.removeEventListener('cart:change', this._onCartUpdated);
      document.removeEventListener('on:line-item:change', this._onCartUpdated);
      document.removeEventListener('dispatch:cart-drawer:open', this._onDrawerOpen);
      if (this._drawerOpenObserver) {
        this._drawerOpenObserver.disconnect();
        this._drawerOpenObserver = null;
      }
    }

    restore() {
      const stored = DeliveryUtils.readLocation();
      if (stored && stored.postcode) {
        const label = DeliveryUtils.getLocationLabel(stored);
        if (this.locationEl) this.locationEl.textContent = label;

        // [May 9, 2026] Cart page auto-fetch via URL pattern instead of
        // body class check. The previous `body.classList.contains('template-cart')`
        // returned false on this theme (verified - body has no template-cart
        // class). URL pattern works regardless of body-class conventions.
        const isCartPage = /(^|\/)cart\/?$/.test(location.pathname);
        if (isCartPage) {
          this.fetchAndDisplay(stored.postcode);
        }
      }
    }

    async refreshCart() {
      const stored = DeliveryUtils.readLocation();
      if (stored && stored.postcode) {
        this.fetchAndDisplay(stored.postcode);
      }
    }

    async fetchAndDisplay(postcode) {
      // [May 9, 2026] Single-flight guard. Multiple event listeners
      // (cart:updated, cart:change, on:line-item:change, drawer-open
      // observer, location-change) can all fire close together and
      // each call fetchAndDisplay. Concurrent /cart/shipping_rates.json
      // requests cause 409 Conflict errors on Shopify's side because
      // shipping rate calc acquires a cart-level lock. Single-flight
      // ensures only one fetch is in flight at a time. If a new call
      // comes in mid-flight, queue ONE follow-up to run after.
      if (this._inFlight) {
        this._pendingPostcode = postcode;
        return;
      }
      this._inFlight = true;
      try {
        await this._doFetchAndDisplay(postcode);
      } finally {
        this._inFlight = false;
        if (this._pendingPostcode) {
          const pending = this._pendingPostcode;
          this._pendingPostcode = null;
          this.fetchAndDisplay(pending);
        }
      }
    }

    async _doFetchAndDisplay(postcode) {
      const state = DeliveryUtils.stateFromPostcode(postcode);
      const stored = DeliveryUtils.readLocation();
      const label = DeliveryUtils.getLocationLabel(stored || { postcode, suburb: state.short });
      if (this.locationEl) this.locationEl.textContent = label;

      let cart;
      try {
        const res = await fetch('/cart.js');
        if (!res.ok) throw new Error(`Cart fetch failed (${res.status})`);
        cart = await res.json();
      } catch (error) {
        this.showDeliveryError(error);
        return;
      }

      if (!cart.item_count) {
        this.rateContainer.hidden = true;
        this.milestone.hidden = true;
        this.localContainer.hidden = true;
        this.loadingEl.hidden = true;
        this.classList.remove('is-calculating');
        return;
      }

      const zone = this.qwikiConfig.zones.find((candidate) => candidate.postcodes.includes(String(postcode)));
      if (zone) {
        this.renderQwikiDelivery(cart, zone);
        return;
      }

      this.localContainer.hidden = true;
      this.milestone.hidden = true;
      this.poboxNotice.hidden = false;
      this.moreText.textContent = this.defaultMoreText;
      // [May 12, 2026] Keep .dec__rate visible during fetch to prevent the
      // ~60px layout shift that happened when the whole block hid and was
      // replaced by the smaller "Calculating..." text. Now only the price
      // text changes; headroom + per-item retain stale values briefly and
      // refresh after fetch completes.
      this.loadingEl.hidden = true;
      this.rateContainer.hidden = false;
      this.classList.add('is-calculating');
      // Only show "Calculating…" if this is the first fetch (no prior value).
      // On subsequent fetches, keep the stale price visible (dimmed via CSS)
      // so it doesn't flicker between states.
      if (!this.rateText.textContent || !this.rateText.textContent.trim()) {
        this.rateText.textContent = 'Calculating…';
      }

      // Fetch variant weights before proceeding with calculations
      const variantIds = cart.items.map(item => item.variant_id);
      const variantWeights = await DeliveryUtils.fetchVariantWeights(variantIds);

      // Map weights back to cart items
      const itemsWithWeights = cart.items.map(item => ({
        ...item,
        weight: variantWeights[item.variant_id] || 0
      }));

      const params = new URLSearchParams({
        'shipping_address[zip]': postcode,
        'shipping_address[country]': state.country,
        'shipping_address[province]': state.province,
      });

      try {
        const res = await fetch(`/cart/shipping_rates.json?${params}`);
        if (!res.ok) throw new Error('Rate fetch failed');
        const data = await res.json();

        if (data.shipping_rates && data.shipping_rates.length > 0) {
          this.rateContainer.classList.remove('is-unavailable');
          const cheapest = data.shipping_rates.reduce((min, r) =>
            parseFloat(r.price) < parseFloat(min.price) ? r : min
          );
          const currentPrice = parseFloat(cheapest.price);

          // Rate text with current weight
          const weightG = cart.total_weight;
          const weightLabel = weightG < 1000
            ? `${Math.round(weightG)}g`
            : `${(weightG / 1000).toFixed(1)}kg`;
          // Show weight tier progress
          const tierInfo = DeliveryUtils.tierHeadroom(weightG);
          const tierLabel = tierInfo.currentTier < 1000
            ? `${Math.round(tierInfo.currentTier)}g`
            : `${(tierInfo.currentTier / 1000).toFixed(1)}kg`;

          this.rateText.textContent = currentPrice === 0 ? 'Free shipping!' : `Shipping: $${currentPrice.toFixed(2)}`;

          // Per-item cost
          if (currentPrice > 0 && cart.item_count > 0) {
            this.perItemEl.textContent = `($${(currentPrice / cart.item_count).toFixed(2)}/item)`;
          } else {
            this.perItemEl.textContent = '';
          }
          if (tierInfo.headroomG > 0) {
            this.renderWeightProgressBar(weightG, tierInfo.currentTier, tierInfo.headroomG, currentPrice, itemsWithWeights);
          } else {
            this.headroomEl.hidden = true;
          }

          this.rateContainer.hidden = false;
        } else {
          if (!Array.isArray(data.shipping_rates)) throw new Error('Invalid shipping rate response');
          this.renderNoDeliveryOptions();
        }
      } catch (err) {
        this.showDeliveryError(err);
      }

      this.loadingEl.hidden = true;
      this.classList.remove('is-calculating');
    }

    renderQwikiDelivery(cart, zone) {
      const config = this.qwikiConfig;
      const remaining = Math.max(0, config.freeDeliveryCents - cart.total_price);
      const money = (cents) => `$${(cents / 100).toFixed(2)}`;
      const date = DeliveryUtils.nextQwikiDelivery(zone, config);
      const dateLabel = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      }).format(date);

      this.localZoneEl.textContent = `Qwiki - ${zone.name}`;
      this.localDateEl.textContent = `Next delivery: ${dateLabel}`;
      this.localContainer.hidden = false;
      this.rateContainer.hidden = true;
      this.headroomEl.hidden = true;
      this.perItemEl.textContent = '';
      this.poboxNotice.hidden = true;
      this.moreText.textContent = `Qwiki ${zone.name}. Order by 7:00 AM on your delivery day, subject to route capacity. Delivery is ${money(config.deliveryFeeCents)}, or free on orders from ${money(config.freeDeliveryCents)}. Final options and charges are confirmed at checkout.`;

      this.milestone.hidden = false;
      this.milestone.classList.toggle('is-unlocked', remaining === 0);
      this.milestoneMsg.textContent = remaining > 0
        ? `Buy ${money(remaining)} more to get FREE local delivery`
        : 'FREE local delivery unlocked!';
      this.milestoneFill.style.width = `${Math.min(100, cart.total_price / config.freeDeliveryCents * 100)}%`;
      this.milestoneProgress.hidden = false;
      this.milestoneProgress.textContent = `${money(cart.total_price)} of ${money(config.freeDeliveryCents)}`;
      this.milestone.querySelectorAll('.dec__weight-row, .dec__weight-bar, .js-overweight-note').forEach((element) => {
        element.hidden = true;
      });
      this.rateFooter.hidden = remaining === 0;
      this.rateFooterLabel.textContent = 'Local delivery:';
      this.rateFooterPrice.textContent = money(config.deliveryFeeCents);
      this.loadingEl.hidden = true;
      this.classList.remove('is-calculating');
    }

    renderNoDeliveryOptions() {
      const localOnly = this.dataset.hasLocalOnlyItems === 'true';
      this.rateContainer.classList.add('is-unavailable');
      this.rateText.textContent = localOnly
        ? 'Fresh and frozen items are local delivery only'
        : 'No delivery options for this cart';
      this.perItemEl.textContent = '';
      if (localOnly) {
        this.headroomEl.innerHTML = 'Remove fresh and frozen items or choose a <a class="dec__qwiki-link" href="/pages/qwiki#qwiki-postcodes">Qwiki postcode</a>.';
      } else {
        this.headroomEl.textContent = 'Check your postcode or contact us for help.';
      }
      this.headroomEl.hidden = false;
      this.rateContainer.hidden = false;
    }

    handleQwikiLinkClick(event) {
      const link = event.target.closest('.dec__qwiki-link');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const checker = document.getElementById('qwiki-postcodes');
      const drawer = this.closest('cart-drawer');
      if (!checker) {
        if (drawer) drawer.close();
        return;
      }

      event.preventDefault();
      const revealChecker = () => {
        history.replaceState(null, '', link.getAttribute('href'));
        checker.scrollIntoView({ block: 'start' });
        const input = checker.parentElement.querySelector('input');
        if (input) input.focus({ preventScroll: true });
      };
      if (drawer && drawer.hasAttribute('open') && !drawer.isDocked) {
        drawer.close(revealChecker);
      } else {
        revealChecker();
      }
    }

    showDeliveryError(error) {
      console.error('[Delivery estimate]', error);
      this.rateContainer.classList.add('is-unavailable');
      this.localContainer.hidden = true;
      this.milestone.hidden = true;
      this.rateContainer.hidden = false;
      this.rateText.textContent = 'Delivery estimate unavailable. Confirm delivery at checkout.';
      this.perItemEl.textContent = '';
      this.headroomEl.hidden = true;
      this.loadingEl.hidden = true;
      this.classList.remove('is-calculating');
    }

    renderPriceProgressBar(currentWeightG, nextPriceWeightG, currentPrice, nextPrice, headroomG, items) {
      const percentUsed = (currentWeightG / nextPriceWeightG) * 100;

      this.headroomEl.innerHTML = `
        <div class="cart-progress-bar">
          <div class="cart-progress-fill" style="width: ${Math.min(100, percentUsed)}%"></div>
        </div>
        <p class="cart-progress-label">Next tier: $${parseFloat(nextPrice).toFixed(2)}</p>
      `;
      this.headroomEl.hidden = false;
    }

    renderWeightProgressBar(currentWeightG, tierCeilingG, headroomG, currentPrice, items) {
      // Show on first load before we have price data
      const percentUsed = (currentWeightG / tierCeilingG) * 100;

      const currentLabel = currentWeightG < 1000
        ? `${Math.round(currentWeightG)}g`
        : `${(currentWeightG / 1000).toFixed(1)}kg`;
      const tierLabel = tierCeilingG < 1000
        ? `${tierCeilingG}g`
        : `${(tierCeilingG / 1000).toFixed(1)}kg`;

      this.headroomEl.innerHTML = `
        <div class="cart-progress-bar">
          <div class="cart-progress-fill" style="width: ${percentUsed}%"></div>
        </div>
        <p class="cart-progress-label">${currentLabel} of ${tierLabel}</p>
      `;
      this.headroomEl.hidden = false;
    }

  }
  customElements.define('delivery-estimate-cart', DeliveryEstimateCart);
}
